import React, { useRef } from 'react'
import { Box, ButtonBase } from '@mui/material'
import { color, focusRing, radius } from './tokens'

/**
 * Horizontal pill control, not a second left nav: the admin already has a
 * sidebar, and two vertical navs side by side read as clutter. Scrolls
 * sideways on a phone. Arrow keys move between sections (WAI-ARIA tabs).
 */
export default function SectionNav({ sections, value, onChange }) {
  const refs = useRef({})

  const onKeyDown = (e, index) => {
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!delta) return
    e.preventDefault()
    const next = sections[(index + delta + sections.length) % sections.length]
    onChange(next.key)
    refs.current[next.key]?.focus()
  }

  return (
    <Box
      role="tablist"
      aria-label="Payments sections"
      sx={{
        display: 'flex',
        gap: 0.5,
        p: 0.5,
        bgcolor: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius.card,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        width: { xs: '100%', md: 'fit-content' },
      }}
    >
      {sections.map(({ key, label, Icon }, i) => {
        const active = key === value
        return (
          <ButtonBase
            key={key}
            ref={(el) => { refs.current[key] = el }}
            role="tab"
            id={`payments-tab-${key}`}
            aria-selected={active}
            aria-controls={`payments-panel-${key}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(key)}
            onKeyDown={(e) => onKeyDown(e, i)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.9,
              px: 1.75,
              py: 1,
              borderRadius: radius.control,
              fontSize: 14,
              fontWeight: active ? 600 : 500,
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              color: active ? color.accent : color.muted,
              bgcolor: active ? color.accentSoft : 'transparent',
              transition: 'background-color 160ms ease, color 160ms ease',
              '&:hover': { bgcolor: active ? color.accentSoft : '#F3F5F8', color: active ? color.accent : color.text },
              ...focusRing,
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
            {label}
          </ButtonBase>
        )
      })}
    </Box>
  )
}
